// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraDirection, CameraSource } from "@capacitor/camera";
import { type BadgeOrderImageSource } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useContractorBadgePictureUpload } from "@src/appV2/Agents/api/useContractorBadgePictureUpload";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logError } from "@src/appV2/lib/analytics";
import { base64ToBlob } from "@src/appV2/lib/files";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect, useState } from "react";

import { IGNORED_FILE_UPLOAD_ERROR_MESSAGES } from "../../Documents/Details/components/FileSelectionBottomSheet";
import { useGetAllPossiblePicturesForBadge } from "../../IndependantContractorBadge/useGetAllPossiblePicturesForBadge";

interface UseWorkBadgeSelectHeadshotParams {
  onConfirmBadgePhoto: (params: {
    workerHeadshotBase64: string;
    imageSource: BadgeOrderImageSource;
  }) => void;
}

export function useWorkBadgeSelectHeadshot(params: UseWorkBadgeSelectHeadshotParams) {
  const { onConfirmBadgePhoto } = params;
  const badgePhotoSelectionModalState = useModalState();
  const cameraPermissionErrorModalState = useModalState();
  const imageCropperModalState = useModalState();
  const worker = useDefinedWorker();

  const { showErrorToast } = useToast();

  const [workerHeadshotBase64, setWorkerHeadshotBase64] = useState<string | undefined>();
  const [selectedImageSource, setSelectedImageSource] = useState<BadgeOrderImageSource>(
    "contractor-badge-picture"
  );
  const [croppingImage, setCroppingImage] = useState<
    | {
        dataUrlBase64: string;
        source: CameraSource;
      }
    | undefined
  >(undefined);
  const [isUploadingBadgePhoto, setIsUploadingBadgePhoto] = useState(false);

  const possiblePicturesForBadge = useGetAllPossiblePicturesForBadge({
    workerId: worker.userId,
    options: {
      refetchOnWindowFocus: false,
    },
  });

  const { mutateAsync: uploadBadgePicture } = useContractorBadgePictureUpload();

  useEffect(() => {
    if (possiblePicturesForBadge.lastSavedBadgePictureBase64 && !workerHeadshotBase64) {
      setWorkerHeadshotBase64(possiblePicturesForBadge.lastSavedBadgePictureBase64);
    }
  }, [possiblePicturesForBadge.lastSavedBadgePictureBase64, workerHeadshotBase64]);

  // We added `isFetchingProfilePicture` and `isFetchingLastSavedBadgePicture` to fix a bug.
  //
  // First visit:
  //   1. Profile picture query runs → status = "loading" → we show loader
  //   2. Query succeeds → returns presigned URL → base64 query runs → status = "loading"
  //   3. Base64 query succeeds → we show the badge
  //   This works fine.
  //
  // Second visit (the bug):
  //   1. Profile picture query has cached data → status = "success" → we show the badge immediately
  //   2. But a background refetch is running (isFetching = true, status = "success")
  //   3. Background refetch succeeds → returns a NEW presigned URL (different from cached one)
  //   4. Base64 query sees new URL → no cache for this URL → status = "loading"
  //   5. Now we show loader AFTER the badge was already shown → confusing!
  //
  // Fix: Check `isFetchingProfilePicture` and `isFetchingLastSavedBadgePicture`.
  // These are true when background refetch is running, even if status = "success".
  // By showing loader during background refetch, we avoid the "badge → loader → badge" flash.
  const isPicturesLoading =
    possiblePicturesForBadge.isFetchingProfilePicture ||
    possiblePicturesForBadge.isFetchingLastSavedBadgePicture ||
    possiblePicturesForBadge.profilePictureBase64Status === "loading" ||
    possiblePicturesForBadge.lastSavedBadgePictureBase64Status === "loading";

  // Profile picture is defined if it is not an empty string
  const hasProfilePicture = Boolean(possiblePicturesForBadge.profilePictureBase64);

  function handleUseProfilePictureForBadge() {
    if (hasProfilePicture) {
      setWorkerHeadshotBase64(possiblePicturesForBadge.profilePictureBase64);
      setSelectedImageSource("profile-picture");
      badgePhotoSelectionModalState.closeModal();
    } else {
      // It will not happen as we check if the user has a profile picture in the UI and only show the option if they do
      showErrorToast(
        "You do not have a profile picture. Please upload one to use it for your badge."
      );
    }
  }

  const onError = (error: Error | CameraPermissionDeniedError, source: CameraSource) => {
    if (error instanceof CameraPermissionDeniedError) {
      badgePhotoSelectionModalState.closeModal();
      cameraPermissionErrorModalState.openModal();
    } else {
      if (Object.values(IGNORED_FILE_UPLOAD_ERROR_MESSAGES).includes(error?.message)) {
        return;
      }

      logError(APP_V2_USER_EVENTS.WORK_BADGE_PHOTO_SELECTION_FAILURE, {
        error,
        metadata: {
          source,
        },
      });

      // show error to user
      showErrorToast(
        error.message ?? "We're having trouble processing your photo. Please try again."
      );
    }
  };

  const onSuccess = (photo: UploadFileData, source: CameraSource) => {
    badgePhotoSelectionModalState.closeModal();
    setCroppingImage({
      dataUrlBase64: photo.dataUrlBase64,
      source,
    });
    imageCropperModalState.openModal();
  };

  // Our mobile devices are usually 512px wide, so we take max width as 512px
  // This will ensure reduced image resolution and thus size is reduced
  const maxWidth = 512;

  const handleTakePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess: (photo) => {
      onSuccess(photo, CameraSource.Camera);
    },
    onError: (error) => {
      onError(error, CameraSource.Camera);
    },
    photoOptions: {
      direction: CameraDirection.Front,
      // Plugin param is not named good.
      // This param is to define max width while preserving aspect ratio of camera image
      width: maxWidth,
    },
  });

  const handleSelectPhotoFromCameraRoll = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess: (photo) => {
      onSuccess(photo, CameraSource.Photos);
    },
    onError: (error) => {
      onError(error, CameraSource.Photos);
    },
  });

  const onCropComplete = (croppedImageBase64: string) => {
    setWorkerHeadshotBase64(croppedImageBase64);
    setSelectedImageSource("contractor-badge-picture");
    imageCropperModalState.closeModal();
  };

  const handleRetryCropping = async () => {
    imageCropperModalState.closeModal();
    if (croppingImage?.source === CameraSource.Camera) {
      await handleTakePhoto();
    } else if (croppingImage?.source === CameraSource.Photos) {
      await handleSelectPhotoFromCameraRoll();
    } else {
      showErrorToast("Failed to retry cropping. Please try again.");
    }
  };

  async function handleConfirmBadgePhoto() {
    if (!isDefined(workerHeadshotBase64)) {
      showErrorToast("Please select a badge photo to confirm.");
      return;
    }

    if (workerHeadshotBase64 === possiblePicturesForBadge.lastSavedBadgePictureBase64) {
      onConfirmBadgePhoto({ workerHeadshotBase64, imageSource: selectedImageSource });
      return;
    }

    setIsUploadingBadgePhoto(true);
    try {
      const file = await base64ToBlob(workerHeadshotBase64);

      await uploadBadgePicture({
        file,
        workerId: worker.userId,
      });

      onConfirmBadgePhoto({ workerHeadshotBase64, imageSource: selectedImageSource });
    } catch (error) {
      showErrorToast("Failed to upload badge photo. Please try again.");
      logError(APP_V2_APP_EVENTS.WORK_BADGE_PHOTO_UPLOAD_FAILURE, {
        error,
      });
    } finally {
      possiblePicturesForBadge.resetQueries();
      setIsUploadingBadgePhoto(false);
    }
  }

  return {
    badgePhotoSelectionModalState,
    isPicturesLoading,
    workerHeadshotBase64,
    hasProfilePicture,
    handleUseProfilePictureForBadge,
    handleTakePhoto,
    handleSelectPhotoFromCameraRoll,
    imageCropperModalState,
    cameraPermissionErrorModalState,
    onCropComplete,
    croppingImage,
    handleRetryCropping,
    handleConfirmBadgePhoto,
    isUploadingBadgePhoto,
  };
}
